import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { Navigation } from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/documents`, {
        withCredentials: true,
      });
      setDocuments(data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleMarkSigned = async (docId) => {
    try {
      await axios.post(
        `${API}/documents/mark-signed`,
        { document_id: docId },
        { withCredentials: true }
      );
      fetchDocuments();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error marking document signed:', error);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Delete document "${doc.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/documents/${doc.id}`, { withCredentials: true });
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      alert('Failed to delete document. Please try again.');
      if (process.env.NODE_ENV === 'development') console.error(err);
    }
  };

  const filtered = documents.filter((doc) => {
    if (filter === 'all') return true;
    if (filter === 'contracts') return doc.doc_type === 'contract';
    if (filter === 'releases') return doc.doc_type === 'model_release';
    return doc.doc_type === 'other' || (doc.doc_type !== 'contract' && doc.doc_type !== 'model_release');
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="max-w-2xl mx-auto p-4">
          <div className="text-text-secondary">Loading...</div>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-1" data-testid="documents-title">
              Documents
            </h1>
            <p className="text-text-secondary">{documents.length} total</p>
          </div>
          <button
            onClick={() => navigate('/documents/new')}
            className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 flex items-center gap-2 transition-colors"
            data-testid="add-document-button"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto">
          {['all', 'contracts', 'releases', 'other'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-primary text-background'
                  : 'bg-surface text-text-secondary hover:text-foreground hover:bg-surface-raised border border-border'
              }`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center" data-testid="documents-empty">
            <p className="text-text-secondary mb-4">
              {filter === 'all' ? 'No documents yet' : `No ${filter} found`}
            </p>
            {filter === 'all' && (
              <button
                onClick={() => navigate('/documents/new')}
                className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 transition-colors"
              >
                Add Your First Document
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((doc) => (
              <div
                key={doc.id}
                className="bg-surface border border-border rounded-xl p-4"
                data-testid={`document-card-${doc.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground">{doc.title}</h3>
                      {doc.status === 'sent' && (
                        <button
                          onClick={() => handleMarkSigned(doc.id)}
                          className="text-xs bg-success/10 text-success px-2 py-1 rounded border border-success/20 hover:bg-success/20 transition-colors"
                          data-testid={`mark-signed-${doc.id}`}
                        >
                          Mark Signed
                        </button>
                      )}
                      <StatusBadge status={doc.status} />
                    </div>
                    <button
                      onClick={() => navigate(`/clients/${doc.client_id}`)}
                      className="text-sm text-text-secondary hover:text-primary transition-colors"
                    >
                      {doc.client_name}
                    </button>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-text-muted capitalize">{doc.doc_type.replace('_', ' ')}</span>
                      {doc.expiry_date && (
                        <span className="text-xs text-text-muted">Expires: {doc.expiry_date}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument(doc)}
                    className="p-2 -mr-1 -mt-1 text-text-muted hover:text-alert hover:bg-alert/10 rounded-lg transition-colors"
                    aria-label={`Delete document ${doc.title}`}
                    data-testid={`delete-document-${doc.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Navigation />
    </div>
  );
}
